// Reports sidebar (design spec 2026-10-19 §4): All reports, then FINANCE (M5-04; KPIS-MODE,
// founder-approved 2026-10-22: moved here from the Chart sidebar) — budget vs actual, money per
// seat, forecast, scenarios, headcount plan, exports, each shown only to a viewer its route
// answers — then the WEEKS caption with one row per report week from `reports.list` (the list
// page's own query, so the list page adds no call): the week, its latest version's state, an
// amber dot when it is stale. Client viewers see sent weeks only (the server filters). "Load
// more" follows the cursor on request. The Friday reports are `kpi`'s: without it only Finance.
// FOUNDER-OS phase 1 (design §3.2): in Founder OS, Live (the portfolio picture) leads and the
// Friday-report rows (All reports, Weeks) are gone — the portfolio company has none of its own.
import { Badge, NavList, NavRow, SidebarCaption, SidebarPanel } from "@companyos/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useRouteContext } from "@tanstack/react-router";
import {
  ActivityIcon,
  BotIcon,
  ClipboardListIcon,
  CoinsIcon,
  DownloadIcon,
  FileTextIcon,
  FlaskConicalIcon,
  Settings2Icon,
  TrendingUpIcon,
  WalletIcon,
} from "lucide-react";
import { canOpen } from "../../extensions/modes";
import type { SidebarProps } from "../../extensions/registry";
import { isManager } from "../../features/company";
import { financeAccessQuery } from "../../features/finance/finance-queries";
import { REPORT_STATE_LABELS } from "../../features/format";
import { reportsQuery } from "../../features/queries";
import { latestByWeek, REPORT_STATE_VARIANT } from "../../features/reports/report-state";
import { useFeature, useSession } from "../../features/use-feature";
import { keepFocus } from "../../focus/focus";

/**
 * Reports › Finance (M5-04), in the order a month's review reads them. `needs` is the standing
 * the page's route holds the caller to (`financeAccess.list` `me`): `totals` (managers, finance
 * admins, a seat holder's own subtree) or `restricted` (the owner and finance admins).
 */
export const FINANCE_ROWS = [
  {
    to: "/c/$slug/kpis/reports/finance",
    label: "Budget vs actual",
    icon: WalletIcon,
    needs: "totals",
  },
  {
    to: "/c/$slug/kpis/reports/finance/margins",
    label: "Money per seat",
    icon: CoinsIcon,
    needs: "restricted",
  },
  {
    to: "/c/$slug/kpis/reports/finance/agent-spend",
    label: "Agent spend",
    icon: BotIcon,
    needs: "totals",
  },
  {
    to: "/c/$slug/kpis/reports/finance/forecast",
    label: "Forecast",
    icon: TrendingUpIcon,
    needs: "totals",
  },
  {
    to: "/c/$slug/kpis/reports/finance/scenarios",
    label: "Scenarios",
    icon: FlaskConicalIcon,
    needs: "restricted",
  },
  {
    to: "/c/$slug/kpis/reports/finance/headcount",
    label: "Headcount plan",
    icon: ClipboardListIcon,
    needs: "restricted",
  },
  {
    to: "/c/$slug/kpis/reports/finance/exports",
    label: "Exports",
    icon: DownloadIcon,
    needs: "restricted",
  },
] as const;

export function ReportsSidebar({ company, children }: SidebarProps) {
  const params = { slug: company.slug };
  // The Friday reports are the readers' with `kpi`; a member opens Reports for Finance only.
  // FOUNDER-OS phase 1 (design §3.2): Founder OS has no Friday reports of its own; Live leads.
  const portfolio = company.kind === "portfolio";
  return (
    <SidebarPanel
      title="Reports"
      footer={
        isManager(company) && !portfolio ? (
          <NavList>
            <ReportSettingsRow slug={params.slug} />
          </NavList>
        ) : null
      }
    >
      <ReportsSection company={company} />
      {children}
    </SidebarPanel>
  );
}

/** Managers: Setup › General's Friday report settings. */
function ReportSettingsRow({ slug }: { slug: string }) {
  return (
    <NavRow
      icon={Settings2Icon}
      label="Report settings"
      render={
        <Link
          to="/c/$slug/setup/general"
          params={{ slug }}
          search={keepFocus}
          hash="friday-report"
          activeOptions={{ includeSearch: false, includeHash: true }}
        />
      }
    />
  );
}

/**
 * NAV-REDESIGN (2026-10-29): Reports' rows — Live (Founder OS) or All reports, Finance, the
 * weeks — drawn in the KPIs sidebar while a Reports page is open (`/kpis/reports/…`), with
 * Report settings for managers (`settings`; the Reports sidebar keeps it in its footer).
 */
export function ReportsSection({
  company,
  settings = false,
}: Pick<SidebarProps, "company"> & { settings?: boolean }) {
  const params = { slug: company.slug };
  const { me } = useSession();
  const portfolio = company.kind === "portfolio";
  const kpi = useFeature("kpi").enabled;
  const weeks = !portfolio && kpi && canOpen("report-weeks", me, company);
  return (
    <>
      {settings && isManager(company) && !portfolio ? (
        <NavList data-slot="report-settings">
          <ReportSettingsRow slug={params.slug} />
        </NavList>
      ) : null}
      {portfolio ? (
        <NavList>
          <NavRow
            icon={ActivityIcon}
            label="Live"
            render={
              <Link
                to="/c/$slug/kpis/reports/live"
                params={params}
                search={keepFocus}
                activeOptions={{ exact: true, includeSearch: false }}
              />
            }
          />
        </NavList>
      ) : null}
      {weeks ? (
        <NavList>
          <NavRow
            icon={FileTextIcon}
            label="All reports"
            render={
              <Link
                to="/c/$slug/kpis/reports"
                params={params}
                search={keepFocus}
                activeOptions={{ exact: true, includeSearch: false }}
              />
            }
          />
        </NavList>
      ) : null}
      <FinanceRows slug={company.slug} companyId={company.id} />
      {weeks ? <ReportWeeks companyId={company.id} slug={company.slug} /> : null}
    </>
  );
}

/** FINANCE: the pages the viewer's finance standing answers (none: no caption at all). */
function FinanceRows({ slug, companyId }: { slug: string; companyId: string }) {
  const { api } = useRouteContext({ from: "__root__" });
  // Wave 8: a finance page shows only to a viewer its route answers.
  const access = useQuery({ ...financeAccessQuery(api, companyId), retry: false });
  const me = access.data?.me;
  const rows = FINANCE_ROWS.filter((r) =>
    r.needs === "totals" ? me?.reads_totals : me?.reads_restricted,
  );
  if (!rows.length) return null;
  return (
    <>
      <SidebarCaption label="Finance" />
      <NavList aria-label="Finance" data-slot="finance-nav">
        {rows.map((r) => (
          <NavRow
            key={r.to}
            icon={r.icon}
            label={r.label}
            render={
              <Link
                to={r.to}
                params={{ slug }}
                search={keepFocus}
                activeOptions={{ exact: true, includeSearch: false }}
              />
            }
          />
        ))}
      </NavList>
    </>
  );
}

function ReportWeeks({ companyId, slug }: { companyId: string; slug: string }) {
  const { api } = useRouteContext({ from: "__root__" });
  const reports = useInfiniteQuery(reportsQuery(api, companyId));
  const weeks = latestByWeek(reports.data?.pages.flatMap((p) => p.data) ?? []);
  return (
    <>
      <SidebarCaption label="Weeks" />
      {reports.isPending ? null : reports.isError ? (
        <p className="px-4 text-xs text-muted-foreground">Weeks could not be loaded.</p>
      ) : weeks.length === 0 ? (
        <p className="px-4 text-xs text-muted-foreground">No Friday reports yet</p>
      ) : (
        <NavList>
          {weeks.map((r) => (
            <NavRow
              key={r.week_key}
              label={`W${r.week_key.slice(-2)} · ${r.week_key.slice(0, 4)}`}
              trailing={
                <span className="flex items-center gap-1.5">
                  {r.report_stale && r.state === "generated" ? (
                    <span
                      className="size-1.5 rounded-full bg-warning"
                      role="img"
                      aria-label="stale"
                    />
                  ) : null}
                  <Badge variant={REPORT_STATE_VARIANT[r.state]}>
                    {REPORT_STATE_LABELS[r.state]}
                  </Badge>
                </span>
              }
              render={
                <Link
                  to="/c/$slug/kpis/reports/$week"
                  params={{ slug, week: r.week_key }}
                  search={keepFocus}
                  activeOptions={{ includeSearch: false }}
                />
              }
            />
          ))}
          {reports.hasNextPage ? (
            <NavRow
              label={reports.isFetchingNextPage ? "Loading…" : "Load more weeks"}
              render={<button type="button" onClick={() => void reports.fetchNextPage()} />}
            />
          ) : null}
        </NavList>
      )}
    </>
  );
}
